import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";
import { Spinner } from "./Spinner";

export type ButtonVariante = "primario" | "secundario" | "peligro" | "fantasma";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: ButtonVariante;
  tamanio?: "sm" | "md";
  cargando?: boolean;
  anchoCompleto?: boolean;
}

const VARIANTES: Record<ButtonVariante, string> = {
  primario: "bg-primario text-texto-invertido hover:bg-primario-hover",
  secundario: "bg-transparent text-texto border border-texto/20 hover:bg-texto/5",
  peligro: "bg-peligro text-texto-invertido hover:brightness-95",
  fantasma: "bg-transparent text-primario hover:bg-primario-suave",
};

const TAMANIOS = {
  sm: "h-8 px-3.5 text-chico",
  md: "h-11 px-5 text-cuerpo",
};

// Píldoras, como los de la presentación de Newbery 3563.
const BASE = "inline-flex items-center justify-center gap-2 rounded-full font-medium transition-colors";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variante = "primario", tamanio = "md", cargando = false, anchoCompleto = false, disabled, className, children, type = "button", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || cargando}
      aria-busy={cargando || undefined}
      className={cn(
        BASE,
        "disabled:cursor-not-allowed disabled:opacity-60",
        VARIANTES[variante],
        TAMANIOS[tamanio],
        anchoCompleto && "w-full",
        className,
      )}
      {...props}
    >
      {cargando && <Spinner tamanio="sm" etiqueta="Procesando…" />}
      {children}
    </button>
  );
});

export interface BotonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  variante?: ButtonVariante;
  tamanio?: "sm" | "md";
  /** Abre en otra pestaña (o la app, como WhatsApp) con `rel="noopener noreferrer"`. */
  externo?: boolean;
}

/** Un link que se ve como `Button` (ej. "WhatsApp" en la agenda de proveedores). Para navegar dentro de la app, `Link`. */
export function BotonLink({ href, variante = "secundario", tamanio = "md", externo = false, className, children, ...props }: BotonLinkProps) {
  return (
    <a href={href} {...(externo ? { target: "_blank", rel: "noopener noreferrer" } : {})} className={cn(BASE, VARIANTES[variante], TAMANIOS[tamanio], className)} {...props}>
      {children}
    </a>
  );
}
